/**
 * Validation of the runtime configuration published by App\Tracking\TrackerAssets.
 *
 * Pure: takes the raw value, returns a normalised config or null. Nothing here reads a global, so
 * the whole of the "is this feature switched on" decision is a single testable function.
 */

/** OneTrust group that gates collection when the environment names none. */
export const DEFAULT_CONSENT_GROUP = 'C0002';

/**
 * @typedef {Object} TrackerConfig
 * @property {string}  publishableKey        Site key issued by 3Halves for this domain (`pk_…`).
 * @property {string}  siteId                Site code issued alongside the key.
 * @property {string}  endpoint              Absolute URL of the ingest route.
 * @property {string}  consentGroup          OneTrust group id that must be active to collect.
 * @property {boolean} debug                 Verbose SDK logging, and a window handle for `inspect()`.
 * @property {boolean} requireLoyaltyConsent Whether collection also needs the fan's loyalty opt-in.
 */

/**
 * Coerce a value to a trimmed string.
 *
 * @param {unknown} value Candidate value.
 * @return {string} The trimmed string, or '' for anything that is not a string.
 */
function toTrimmedString( value ) {
	return typeof value === 'string' ? value.trim() : '';
}

/**
 * Interpret a flag that has crossed a stringifying transport.
 *
 * `wp_localize_script` casts every scalar in its payload to a string, so a PHP `true` arrives as
 * `'1'` and a PHP `false` as `''`. Booleans therefore have to be read, not asserted.
 *
 * @param {unknown} value Candidate flag.
 * @return {boolean} The flag.
 */
function toBoolean( value ) {
	if ( typeof value === 'boolean' ) {
		return value;
	}

	return [ '1', 'true', 'yes', 'on' ].includes( String( value ).toLowerCase() );
}

/**
 * Whether a string is an absolute http(s) URL.
 *
 * Guards against a relative or malformed endpoint, which would otherwise resolve against this
 * origin and post fan events at WordPress.
 *
 * @param {string} value Candidate URL.
 * @return {boolean} True when the value parses as an absolute http(s) URL.
 */
function isAbsoluteHttpUrl( value ) {
	try {
		const url = new URL( value );

		return url.protocol === 'http:' || url.protocol === 'https:';
	} catch ( error ) {
		return false;
	}
}

/**
 * Normalise the raw configuration object.
 *
 * Fails closed: a missing key, site id or endpoint returns null, and the caller builds no tracker
 * and issues no request. That is the environment-level kill switch — an unconfigured environment
 * costs one property read.
 *
 * @param {unknown} raw Value published by the server, usually `window.urcScoreTracker`.
 * @return {TrackerConfig|null} The validated config, or null when the feature is not configured.
 */
export function parseConfig( raw ) {
	if ( ! raw || typeof raw !== 'object' ) {
		return null;
	}

	const publishableKey = toTrimmedString( raw.publishableKey );
	const siteId = toTrimmedString( raw.siteId );
	const endpoint = toTrimmedString( raw.endpoint );

	if ( ! publishableKey || ! siteId || ! isAbsoluteHttpUrl( endpoint ) ) {
		return null;
	}

	// **Nothing about identity is configured here any more, and that is a subtraction rather than
	// an omission.** `tokenCookie`, `audience` and `expiryMargin` used to arrive from PHP, and
	// identity was off unless all three did: a cookie name with no audience to check it against
	// would have forwarded whatever it found. The credential comes from `window.urcAuth` now, which
	// either exists or does not and which answers null wherever no loyalty audience is configured --
	// so both of the states that switch protected against degrade to anonymous batches on their
	// own. See ./urcAuth.js.
	return {
		publishableKey,
		siteId,
		endpoint,
		consentGroup: toTrimmedString( raw.consentGroup ) || DEFAULT_CONSENT_GROUP,
		debug: toBoolean( raw.debug ),
		// Follows App\Loyalty\LoyaltyConfig::consentRequested(), so one switch governs both surfaces:
		// while the loyalty consent gate is off, tracking behaves exactly as it did before it
		// existed. Absent -- an environment with no loyalty runtime, or cached markup from before
		// this shipped -- reads as false, which is the pre-consent behaviour and not a fail-closed
		// case: there is no consent surface to fail closed on.
		requireLoyaltyConsent: toBoolean( raw.requireLoyaltyConsent ),
	};
}
